/**
 * Definizione ruoli utente (gerarchia dal più potente al meno)
 */
export const ROLES = {
  ADMIN_GLOBALE: 'admin_globale',
  ADMIN: 'admin',
  CHAT_MANAGER: 'chat_manager'
} as const;

export type RoleType = typeof ROLES[keyof typeof ROLES];

/**
 * Gerarchia ruoli (ordine = potere decrescente)
 * IMPORTANTE: Deve corrispondere a backend/models/userModel.js linea 256
 */
export const ROLE_HIERARCHY: RoleType[] = [
  ROLES.ADMIN_GLOBALE,
  ROLES.ADMIN,
  ROLES.CHAT_MANAGER
];

/**
 * Definizione permessi disponibili nel sistema
 */
export const PERMISSIONS = {
  // Gestione Clienti (solo admin_globale)
  MANAGE_CLIENTS: 'manage_clients',
  VIEW_ALL_CLIENTS: 'view_all_clients',
  
  // Gestione Utenti (admin_globale + admin)
  MANAGE_USERS: 'manage_users',
  CREATE_USERS: 'create_users',
  DELETE_USERS: 'delete_users',
  
  // Gestione Chatbots (admin_globale + admin)
  MANAGE_CHATBOTS: 'manage_chatbots',
  CREATE_CHATBOTS: 'create_chatbots',
  DELETE_CHATBOTS: 'delete_chatbots',
  
  // Conversazioni (tutti)
  VIEW_CONVERSATIONS: 'view_conversations',
  MANAGE_CONVERSATIONS: 'manage_conversations',
  
  // Google Integrations (admin_globale + admin)
  GOOGLE_INTEGRATIONS: 'google_integrations',
  ENABLE_GOOGLE_FEATURES: 'enable_google_features',
  CONFIGURE_GOOGLE_OAUTH: 'configure_google_oauth',
  
  // Dashboard & Analytics (tutti, filtrato per client_id)
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_ANALYTICS: 'view_analytics',
  
  // WhatsApp (admin_globale + admin)
  MANAGE_WHATSAPP: 'manage_whatsapp',

  // WhatsApp Contacts - read & basic ops, opt-in/out (tutti i ruoli)
  VIEW_WHATSAPP_CONTACTS: 'view_whatsapp_contacts',

  // WhatsApp Templates (tutti - chat_manager può gestire solo templates)
  MANAGE_WHATSAPP_TEMPLATES: 'manage_whatsapp_templates',

  // Instagram DM (admin_globale + admin)
  // Permesso dedicato al canale: riusare MANAGE_WHATSAPP darebbe a un ruolo
  // abilitato solo a WhatsApp anche il collegamento degli account Instagram.
  // Deve restare allineato a backend/constants/channelPermissions.ts.
  MANAGE_INSTAGRAM: 'manage_instagram',
  
  // Automations (admin_globale + admin)
  MANAGE_AUTOMATIONS: 'manage_automations',
  
  // Handover & Monitoring (tutti)
  VIEW_HANDOVER: 'view_handover',
  ACCEPT_HANDOVER: 'accept_handover',
  VIEW_MONITORING: 'view_monitoring',
  
  // Flow Builder (solo admin_globale per ora - in fase di test)
  FLOW_BUILDER: 'flow_builder',

  // Audit Trail (solo admin_globale)
  VIEW_AUDIT_TRAIL: 'view_audit_trail'
} as const;

export type PermissionType = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Mappa permessi → ruoli autorizzati
 * Definisce quali ruoli possono avere quali permessi
 */
export const PERMISSION_MAP: Record<PermissionType, RoleType[]> = {
  // Solo admin_globale
  [PERMISSIONS.MANAGE_CLIENTS]: [ROLES.ADMIN_GLOBALE],
  [PERMISSIONS.VIEW_ALL_CLIENTS]: [ROLES.ADMIN_GLOBALE],
  [PERMISSIONS.ENABLE_GOOGLE_FEATURES]: [ROLES.ADMIN_GLOBALE],
  
  // admin_globale + admin
  [PERMISSIONS.MANAGE_USERS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN],
  [PERMISSIONS.CREATE_USERS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN],
  [PERMISSIONS.DELETE_USERS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN],
  [PERMISSIONS.MANAGE_CHATBOTS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN],
  [PERMISSIONS.CREATE_CHATBOTS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN],
  [PERMISSIONS.DELETE_CHATBOTS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN],
  [PERMISSIONS.GOOGLE_INTEGRATIONS]: [ROLES.ADMIN_GLOBALE],
  [PERMISSIONS.CONFIGURE_GOOGLE_OAUTH]: [ROLES.ADMIN_GLOBALE],
  [PERMISSIONS.MANAGE_WHATSAPP]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN],
  [PERMISSIONS.VIEW_WHATSAPP_CONTACTS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN, ROLES.CHAT_MANAGER],
  [PERMISSIONS.MANAGE_WHATSAPP_TEMPLATES]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN, ROLES.CHAT_MANAGER],
  [PERMISSIONS.MANAGE_INSTAGRAM]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN],
  [PERMISSIONS.MANAGE_AUTOMATIONS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN],
  
  // Tutti i ruoli autenticati
  [PERMISSIONS.VIEW_CONVERSATIONS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN, ROLES.CHAT_MANAGER],
  [PERMISSIONS.MANAGE_CONVERSATIONS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN, ROLES.CHAT_MANAGER],
  [PERMISSIONS.VIEW_DASHBOARD]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN, ROLES.CHAT_MANAGER],
  [PERMISSIONS.VIEW_ANALYTICS]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN, ROLES.CHAT_MANAGER],
  [PERMISSIONS.VIEW_HANDOVER]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN, ROLES.CHAT_MANAGER],
  [PERMISSIONS.ACCEPT_HANDOVER]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN, ROLES.CHAT_MANAGER],
  [PERMISSIONS.VIEW_MONITORING]: [ROLES.ADMIN_GLOBALE, ROLES.ADMIN, ROLES.CHAT_MANAGER],
  
  // Flow Builder - solo admin_globale per ora (in fase di test)
  // Per attivare per tutti: aggiungere ROLES.ADMIN, ROLES.CHAT_MANAGER
  [PERMISSIONS.FLOW_BUILDER]: [ROLES.ADMIN_GLOBALE],

  // Audit Trail - solo admin_globale
  [PERMISSIONS.VIEW_AUDIT_TRAIL]: [ROLES.ADMIN_GLOBALE]
};

/**
 * Descrizioni permessi (opzionale, per UI)
 */
export const PERMISSION_DESCRIPTIONS: Partial<Record<PermissionType, string>> = {
  [PERMISSIONS.MANAGE_CLIENTS]: 'Gestione completa clienti (crea, modifica, elimina)',
  [PERMISSIONS.MANAGE_USERS]: 'Gestione utenti del proprio cliente',
  [PERMISSIONS.MANAGE_CHATBOTS]: 'Gestione chatbots (crea, configura, elimina)',
  [PERMISSIONS.GOOGLE_INTEGRATIONS]: 'Accesso e configurazione Google Integrations',
  [PERMISSIONS.VIEW_CONVERSATIONS]: 'Visualizzazione conversazioni'
};
